// src/components/Auth/Register.tsx - LAYOUT HORIZONTAL
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './Login.css';

import logo from '../../assets/images/logo_light.png';
import bgImage from '../../assets/images/left2.png';

interface Plan {
  id: number;
  plan_code: string;
  plan_name: string;
  plan_description: string;
  price_monthly: number;
  price_annual: number;
  currency: string;
  features: string[];
  is_popular: boolean;
  trial_days: number;
}

const Register: React.FC = () => {
  const navigate = useNavigate();
  
  const [step, setStep] = useState<1 | 2>(1);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    company: '',
    country: 'Chile',
    subscription_plan_id: 0
  });

  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [focusedInput, setFocusedInput] = useState<string>('');

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      setLoadingPlans(true);
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      
      const response = await axios.get(`${API_URL}/auth/plans`);
      
      if (response.data.success) {
        setPlans(response.data.plans);
      }
    } catch (err) {
      console.error('Error cargando planes:', err);
      setError('Error cargando planes. Por favor recarga la página.');
    } finally {
      setLoadingPlans(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handlePlanSelect = (plan: Plan) => {
    setSelectedPlan(plan);
    setFormData({ ...formData, subscription_plan_id: plan.id });
    setStep(2);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    if (formData.password.length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres');
      return;
    }

    if (!formData.subscription_plan_id) {
      setError('Debes seleccionar un plan');
      return;
    }

    setLoading(true);

    try {
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      
      const response = await axios.post(`${API_URL}/auth/register`, {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        phone: formData.phone,
        company: formData.company,
        country: formData.country,
        subscription_plan_id: formData.subscription_plan_id
      });

      if (response.data.success) {
        window.location.href = response.data.data.payment_url;
      }
      
      // 👉 Pago pendiente
        if (response.data.code === 'PAYMENT_PENDING') {
          const user = response.data.data.user;
        
          setFormData({
            name: user.name,
            email: user.email,
            phone: user.phone,
            company: user.company,
            country: user.country,
            subscription_plan_id: user.subscription_plan_id,
            password: '' // nunca devolver password
          });
      }

    } catch (err: any) {
      console.error('Error en registro:', err);
      setError(err.response?.data?.message || 'Error en el registro.');
    } finally {
      setLoading(false);
    }
  };

  if (loadingPlans) {
    return (
      <div className="login-container">
        <div className="login-box" style={{ justifyContent: 'center', alignItems: 'center' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ 
              border: '4px solid rgba(255, 115, 24, 0.1)',
              borderTop: '4px solid #FF7318',
              borderRadius: '50%',
              width: '50px',
              height: '50px',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 20px'
            }}></div>
            <p style={{ color: '#ADA4A4' }}>Cargando planes...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-box">
        
        {step === 2 && (
          <div className="login-img">
            <img src={bgImage} alt="KIKAI Background" />
          </div>
        )}

        <div className="login-form" style={{ 
          paddingRight: step === 2 ? '200px' : '0',
          flex: step === 1 ? '1' : undefined,
          width: step === 1 ? '100%' : undefined
        }}>
          <div className="login-content" style={{ width: '100%' }}>
            <div className="login-form-content" style={{ 
              width: step === 1 ? '100%' : '360px',
              maxWidth: step === 1 ? '100%' : '360px'
            }}>
              
              <div className="login-logo">
                <img src={logo} alt="KIKAI Logo" />
              </div>

              {/* ✅ PASO 1: LAYOUT HORIZONTAL DE PLANES */}
             {step === 1 && (
                  <div style={{ 
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '80vh'
                  }}>
                    <h2 className="login-title" style={{ 
                      fontSize: '2rem', 
                      marginBottom: '0.5rem',
                      color: '#fff',
                      fontWeight: '700',
                      textAlign: 'center'
                    }}>
                      SELECCIONA TU PLAN
                    </h2>
                  <p style={{ 
                    color: '#ADA4A4', 
                    marginBottom: '40px', 
                    fontSize: '1rem',
                    textAlign: 'center'
                  }}>
                    Elige el plan que mejor se adapte a tus necesidades
                  </p>

                  {error && (
                    <div className="error-message" style={{ marginBottom: '30px' }}>
                      {error}
                    </div>
                  )}

                  {/* ✅ CONTENEDOR HORIZONTAL CON SCROLL */}
                 <div style={{ 
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'stretch',
                    gap: '30px',
                    marginBottom: '40px',
                    width: '100%',
                    maxWidth: '1200px',
                    padding: '0 20px',
                    flexWrap: 'wrap'
                  }}>
                    {plans.map((plan) => (
                      <div
                        key={plan.id}
                        onClick={() => handlePlanSelect(plan)}
                        style={{
                          width: '340px',
                          flex: '0 0 340px',
                          scrollSnapAlign: 'center',
                          background: 'rgba(255, 255, 255, 0.05)',
                          border: plan.is_popular 
                            ? '3px solid #fce148' 
                            : '2px solid rgba(255, 255, 255, 0.1)',
                          borderRadius: '16px',
                          padding: '30px',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          position: 'relative',
                          backdropFilter: 'blur(10px)',
                          display: 'flex',
                          flexDirection: 'column'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-8px) scale(1.02)';
                          e.currentTarget.style.borderColor = '#fe7217';
                          e.currentTarget.style.boxShadow = '0 15px 35px rgba(255, 115, 24, 0.4)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0) scale(1)';
                          e.currentTarget.style.borderColor = plan.is_popular 
                            ? '#fce148' 
                            : 'rgba(255, 255, 255, 0.1)';
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                      >
                        {plan.is_popular && (
                          <div style={{
                            position: 'absolute',
                            top: '-12px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            background: 'linear-gradient(to right, #fdcf11, #ff7318)',
                            color: '#fff',
                            padding: '6px 20px',
                            borderRadius: '20px',
                            fontSize: '0.75rem',
                            fontWeight: '700',
                            textTransform: 'uppercase',
                            letterSpacing: '1px',
                            boxShadow: '0 4px 10px rgba(253, 207, 17, 0.4)'
                          }}>
                            ⭐ MÁS POPULAR
                          </div>
                        )}

                        <div style={{ textAlign: 'center', marginTop: plan.is_popular ? '15px' : '0' }}>
                          <h3 style={{ 
                            color: '#fce148', 
                            marginBottom: '12px',
                            fontSize: '1.6rem',
                            fontWeight: '700'
                          }}>
                            {plan.plan_name}
                          </h3>
                          
                          <p style={{ 
                            color: '#ADA4A4', 
                            fontSize: '0.9rem',
                            marginBottom: '25px',
                            minHeight: '45px',
                            lineHeight: '1.5'
                          }}>
                            {plan.plan_description}
                          </p>
                          
                          <div style={{ marginBottom: '25px' }}>
                            <div style={{ 
                              fontSize: '1.9rem', 
                              fontWeight: '700',
                              color: '#fff',
                              lineHeight: '1'
                            }}>
                              {plan.currency} ${plan.price_monthly}
                            </div>
                            <div style={{ 
                              color: '#ADA4A4', 
                              fontSize: '0.95rem',
                              marginTop: '5px'
                            }}>
                              por mes
                            </div>
                          </div>

                          <ul style={{ 
                            listStyle: 'none', 
                            padding: 0,
                            marginBottom: '25px',
                            textAlign: 'left'
                          }}>
                            {plan.features.slice(0, 4).map((feature, idx) => (
                              <li key={idx} style={{ 
                                color: '#fff',
                                fontSize: '0.85rem',
                                marginBottom: '12px',
                                paddingLeft: '30px',
                                position: 'relative',
                                lineHeight: '1.4'
                              }}>
                                <span style={{
                                  position: 'absolute',
                                  left: '0',
                                  color: '#38d39f',
                                  fontWeight: 'bold',
                                  fontSize: '1.1rem'
                                }}>✓</span>
                                {feature}
                              </li>
                            ))}
                          </ul>

                          <button
                            style={{
                              width: '100%',
                              padding: '14px',
                              background: plan.is_popular
                                ? 'linear-gradient(to right, #fdcf11, #ff7318)'
                                : 'rgba(255, 255, 255, 0.15)',
                              border: 'none',
                              borderRadius: '10px',
                              color: '#fff',
                              fontWeight: '700',
                              fontSize: '1rem',
                              cursor: 'pointer',
                              transition: 'all 0.3s ease',
                              textTransform: 'uppercase',
                              letterSpacing: '1px'
                            }}
                            onMouseEnter={(e) => {
                              if (plan.is_popular) {
                                e.currentTarget.style.transform = 'scale(1.05)';
                                e.currentTarget.style.boxShadow = '0 8px 20px rgba(255, 115, 24, 0.5)';
                              } else {
                                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (plan.is_popular) {
                                e.currentTarget.style.transform = 'scale(1)';
                                e.currentTarget.style.boxShadow = 'none';
                              } else {
                                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                              }
                            }}
                          >
                            Seleccionar
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                 <p style={{ 
                    textAlign: 'center', 
                    color: '#ADA4A4', 
                    fontSize: '0.95rem',
                    marginTop: '50px',
                    paddingTop: '20px',
                    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                    width: '100%',
                    maxWidth: '600px'
                  }}>
                    ¿Ya tienes cuenta?{' '}
                    <Link 
                      to="/login" 
                      style={{ 
                        color: '#fce148', 
                        textDecoration: 'none',
                        fontWeight: '600'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.color = '#fe7217'}
                      onMouseLeave={(e) => e.currentTarget.style.color = '#fce148'}
                    >
                      Inicia sesión aquí
                    </Link>
                  </p>
                </div>
              )}

              {/* PASO 2: Formulario de Registro (sin cambios) */}
              {step === 2 && (
                <div>
                  <button
                    onClick={() => setStep(1)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#fce148',
                      fontSize: '0.85rem',
                      cursor: 'pointer',
                      marginBottom: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      padding: 0
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#fe7217'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#fce148'}
                  >
                    <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ marginRight: '5px' }}>
                      <path fillRule="evenodd" d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8z"/>
                    </svg>
                    Cambiar plan
                  </button>

                  {selectedPlan && (
                    <div style={{
                      background: 'rgba(252, 225, 72, 0.1)',
                      border: '1px solid rgba(252, 225, 72, 0.3)',
                      borderRadius: '8px',
                      padding: '12px',
                      marginBottom: '20px',
                      textAlign: 'left'
                    }}>
                      <div style={{ fontSize: '0.75rem', color: '#ADA4A4', marginBottom: '5px' }}>
                        Plan seleccionado:
                      </div>
                      <div style={{ color: '#fce148', fontWeight: '600' }}>
                        {selectedPlan.plan_name} - {selectedPlan.currency} ${selectedPlan.price_monthly}/mes
                      </div>
                    </div>
                  )}

                  <h2 className="login-title">COMPLETA TU REGISTRO</h2>

                  {error && (
                    <div className="error-message">
                      {error}
                    </div>
                  )}

                  <form onSubmit={handleSubmit}>
                    {/* Nombre */}
                    <div className={`login-input-div ${focusedInput === 'name' || formData.name ? 'focus' : ''}`}>
                      <div className="login-input-icon">
                        <svg fill="currentColor" viewBox="0 0 16 16">
                          <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm2-3a2 2 0 1 1-4 0 2 2 0 0 1 4 0zm4 8c0 1-1 1-1 1H3s-1 0-1-1 1-4 6-4 6 3 6 4zm-1-.004c-.001-.246-.154-.986-.832-1.664C11.516 10.68 10.289 10 8 10c-2.29 0-3.516.68-4.168 1.332-.678.678-.83 1.418-.832 1.664h10z"/>
                        </svg>
                      </div>
                      <div className="login-input-wrapper">
                        <input
                          type="text"
                          name="name"
                          className="login-input"
                          value={formData.name}
                          onChange={handleChange}
                          onFocus={() => setFocusedInput('name')}
                          onBlur={() => setFocusedInput('')}
                          required
                        />
                        <label className="login-input-label">Nombre completo</label>
                      </div>
                    </div>

                    {/* Email */}
                    <div className={`login-input-div ${focusedInput === 'email' || formData.email ? 'focus' : ''}`}>
                      <div className="login-input-icon">
                        <svg fill="currentColor" viewBox="0 0 16 16">
                          <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V4zm2-1a1 1 0 0 0-1 1v.217l7 4.2 7-4.2V4a1 1 0 0 0-1-1H2zm13 2.383-4.758 2.855L15 11.114v-5.73zm-.034 6.878L9.271 8.82 8 9.583 6.728 8.82l-5.694 3.44A1 1 0 0 0 2 13h12a1 1 0 0 0 .966-.739zM1 11.114l4.758-2.876L1 5.383v5.73z"/>
                        </svg>
                      </div>
                      <div className="login-input-wrapper">
                        <input
                          type="email"
                          name="email"
                          className="login-input"
                          value={formData.email}
                          onChange={handleChange}
                          onFocus={() => setFocusedInput('email')}
                          onBlur={() => setFocusedInput('')}
                          required
                        />
                        <label className="login-input-label">Email</label>
                      </div>
                    </div>

                    {/* Contraseña */}
                    <div className={`login-input-div ${focusedInput === 'password' || formData.password ? 'focus' : ''}`}>
                      <div className="login-input-icon">
                        <svg fill="currentColor" viewBox="0 0 16 16">
                          <path d="M8 1a2 2 0 0 1 2 2v4H6V3a2 2 0 0 1 2-2zm3 6V3a3 3 0 0 0-6 0v4a2 2 0 0 0-2 2v5a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z"/>
                        </svg>
                      </div>
                      <div className="login-input-wrapper">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          name="password"
                          className="login-input"
                          value={formData.password}
                          onChange={handleChange}
                          onFocus={() => setFocusedInput('password')}
                          onBlur={() => setFocusedInput('')}
                          required
                          minLength={8}
                        />
                        <label className="login-input-label">Contraseña</label>
                        <button
                          type="button"
                          className="password-toggle"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? '👁️' : '👁️‍🗨️'}
                        </button>
                      </div>
                    </div>

                    {/* Confirmar Contraseña */}
                    <div className={`login-input-div ${focusedInput === 'confirmPassword' || formData.confirmPassword ? 'focus' : ''}`}>
                      <div className="login-input-icon">
                        <svg fill="currentColor" viewBox="0 0 16 16">
                          <path d="M8 1a2 2 0 0 1 2 2v4H6V3a2 2 0 0 1 2-2zm3 6V3a3 3 0 0 0-6 0v4a2 2 0 0 0-2 2v5a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z"/>
                        </svg>
                      </div>
                      <div className="login-input-wrapper">
                        <input
                          type={showConfirmPassword ? 'text' : 'password'}
                          name="confirmPassword"
                          className="login-input"
                          value={formData.confirmPassword}
                          onChange={handleChange}
                          onFocus={() => setFocusedInput('confirmPassword')}
                          onBlur={() => setFocusedInput('')}
                          required
                        />
                        <label className="login-input-label">Confirmar contraseña</label>
                        <button
                          type="button"
                          className="password-toggle"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        >
                          {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                        </button>
                      </div>
                    </div>

                    {/* Teléfono */}
                    <div className={`login-input-div ${focusedInput === 'phone' || formData.phone ? 'focus' : ''}`}>
                      <div className="login-input-icon">
                        <svg fill="currentColor" viewBox="0 0 16 16">
                          <path fillRule="evenodd" d="M1.885.511a1.745 1.745 0 0 1 2.61.163L6.29 2.98c.329.423.445.974.315 1.494l-.547 2.19a.678.678 0 0 0 .178.643l2.457 2.457a.678.678 0 0 0 .644.178l2.189-.547a1.745 1.745 0 0 1 1.494.315l2.306 1.794c.829.645.905 1.87.163 2.611l-1.034 1.034c-.74.74-1.846 1.065-2.877.702a18.634 18.634 0 0 1-7.01-4.42 18.634 18.634 0 0 1-4.42-7.009c-.362-1.03-.037-2.137.703-2.877L1.885.511z"/>
                        </svg>
                      </div>
                      <div className="login-input-wrapper">
                        <input
                          type="tel"
                          name="phone"
                          className="login-input"
                          value={formData.phone}
                          onChange={handleChange}
                          onFocus={() => setFocusedInput('phone')}
                          onBlur={() => setFocusedInput('')}
                        />
                        <label className="login-input-label">Teléfono (opcional)</label>
                      </div>
                    </div>

                    {/* Empresa */}
                    <div className={`login-input-div ${focusedInput === 'company' || formData.company ? 'focus' : ''}`}>
                      <div className="login-input-icon">
                        <svg fill="currentColor" viewBox="0 0 16 16">
                          <path d="M7 2.5a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1zM0 12.5A1.5 1.5 0 0 0 1.5 14h13a1.5 1.5 0 0 0 1.5-1.5V6.85L8.129 8.947a.5.5 0 0 1-.258 0L0 6.85v5.65z"/>
                          <path d="M0 4.5A1.5 1.5 0 0 1 1.5 3h13A1.5 1.5 0 0 1 16 4.5v1.384l-7.614 2.03a1.5 1.5 0 0 1-.772 0L0 5.884V4.5z"/>
                        </svg>
                      </div>
                      <div className="login-input-wrapper">
                        <input
                          type="text"
                          name="company"
                          className="login-input"
                          value={formData.company}
                          onChange={handleChange}
                          onFocus={() => setFocusedInput('company')}
                          onBlur={() => setFocusedInput('')}
                        />
                        <label className="login-input-label">Empresa (opcional)</label>
                      </div>
                    </div>

                    <div style={{
                      background: 'rgba(56, 211, 159, 0.1)',
                      border: '1px solid rgba(56, 211, 159, 0.3)',
                      borderRadius: '8px',
                      padding: '12px',
                      marginTop: '20px',
                      marginBottom: '20px',
                      fontSize: '0.75rem',
                      color: '#ADA4A4',
                      lineHeight: '1.4'
                    }}>
                      💳 Serás redirigido a PayPal para completar el pago de forma segura. 
                      Tu cuenta se activará automáticamente al confirmar el pago.
                    </div>

                    <button
                      type="submit"
                      className="login-btn"
                      disabled={loading}
                    >
                      {loading ? 'PROCESANDO...' : '💳 CONTINUAR AL PAGO'}
                    </button>

                    <p style={{ textAlign: 'center', color: '#ADA4A4', fontSize: '0.8rem', marginTop: '15px' }}>
                      ¿Ya tienes cuenta?{' '}
                      <Link 
                        to="/login" 
                        className="forgot-password"
                        style={{ display: 'inline' }}
                      >
                        Inicia sesión
                      </Link>
                    </p>
                  </form>
                </div>
              )}

            </div>
          </div>
        </div>

        {step === 2 && <div className="login-pestana"></div>}
      </div>

      <div className="login-footer">
        <div className="footer-content">
          <p className="version">Versión 1.4.0</p>
          <p className="copyright">© 2026 KIKAI - Todos los derechos reservados</p>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

      /* Responsive mejorado */
@media (max-width: 1200px) {
  .login-form-content > div > div:first-child > div:nth-child(3) {
    gap: 20px !important;
  }
  
  .login-form-content > div > div:first-child > div:nth-child(3) > div {
    width: 300px !important;
    flex: 0 0 300px !important;
  }
}

@media (max-width: 992px) {
  .login-form-content > div > div:first-child > div:nth-child(3) {
    flex-direction: column !important;
    align-items: center !important;
  }
  
  .login-form-content > div > div:first-child > div:nth-child(3) > div {
    width: 100% !important;
    max-width: 400px !important;
  }
}

@media (max-width: 768px) {
  .login-form {
    padding-right: 0 !important;
  }
  
  .login-form-content > div > div:first-child {
    min-height: auto !important;
    padding: 20px 0 !important;
  }
}
      `}</style>
    </div>
  );
};

export default Register;